import { Link } from "react-router-dom";
import { ArrowRight, HeartPulse, Sprout, Handshake, HandHeart, Mail } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useSite } from "@/context/SiteContext";
import { IMG, MISSIONS } from "@/lib/content";
import { Reveal, SectionTitle, Placeholder } from "@/components/kit";
import { ArticleCard, useFetch } from "@/components/shared";

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0f1412] text-white lg:min-h-[calc(100svh-72px)]" data-testid="home-hero">
      <div className="relative h-[62svh] overflow-hidden lg:absolute lg:right-0 lg:top-0 lg:h-full lg:w-[52%]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_45%_60%,rgba(224,168,46,0.28),transparent_65%)]" aria-hidden="true" />
        <img src={IMG.maleba} alt="Portrait de Ma Leba, femme âgée souriante portant un foulard rose" fetchPriority="high"
          className="fade-in kenburns relative h-full w-full object-contain object-bottom pt-6 lg:pt-16" data-testid="hero-maleba-photo" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0f1412] to-transparent lg:h-24" />
      </div>
      <div className="absolute inset-0 pagne-light lg:w-[48%]" />
      <div className="container-x relative -mt-24 pb-14 lg:mt-0 lg:flex lg:min-h-[calc(100svh-72px)] lg:items-center lg:py-20">
        <div className="max-w-xl lg:w-[44%]">
          <div className="fade-up mb-5 flex gap-1.5" aria-hidden="true">
            <span className="h-1.5 w-10 bg-ong" /><span className="h-1.5 w-10 bg-gold" /><span className="h-1.5 w-10 bg-asso" />
          </div>
          <h1 className="fade-up font-display text-6xl font-bold uppercase leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl" style={{ animationDelay: ".15s" }} data-testid="hero-title">
            Ma Leba
          </h1>
          <p className="fade-up mt-5 font-display text-xl font-medium uppercase leading-snug tracking-wide text-gold sm:text-2xl" style={{ animationDelay: ".3s" }} data-testid="hero-subtitle">
            Soigner, nourrir, former : ensemble pour l'avenir de son prochain
          </p>
          <div className="fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: ".45s" }}>
            <Link to="/ma-leba" className="btn-gold" data-testid="hero-cta-maleba">Qui était Ma Leba ? <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="btn-outline text-white" data-testid="hero-cta-contact">Nous contacter</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function EntryCard({ to, color, logo, icon: Icon, name, title, text, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group relative flex min-h-[300px] flex-col justify-between overflow-hidden rounded-2xl p-7 text-white shadow-lg transition-transform duration-300 hover:-translate-y-1 sm:p-9" style={{ background: color }}>
      <div className="absolute inset-0 pagne-light" />
      <img src={logo} alt="" loading="lazy" className="absolute -right-6 -top-6 h-44 w-44 rounded-full bg-white/95 object-contain p-4 opacity-95 transition-transform duration-500 group-hover:rotate-6" />
      <div className="relative">
        <Icon className="h-9 w-9 text-white/90" aria-hidden="true" />
        <p className="mt-4 font-display text-sm font-semibold uppercase tracking-[0.25em] text-white/75">{name}</p>
        <h2 className="mt-1 max-w-xs font-display text-3xl font-bold uppercase leading-none sm:text-4xl">{title}</h2>
      </div>
      <div className="relative mt-8 flex items-end justify-between gap-4">
        <p className="max-w-sm text-white/85">{text}</p>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-ink transition-transform group-hover:translate-x-1"><ArrowRight className="h-5 w-5" /></span>
      </div>
    </Link>
  );
}

export default function Home() {
  useSeo(null, "Ma Leba — Soigner, nourrir, former : ensemble pour l'avenir de son prochain. ONG MA LEBA et ASSOCIATION MA LEBA, République du Congo.");
  const { settings } = useSite();
  const m = settings?.maleba || {};
  const news = useFetch("/articles?limit=3");
  return (
    <div data-testid="home-page">
      <Hero />

      <section className="container-x relative z-10 -mt-2 grid gap-5 py-14 md:grid-cols-2 lg:-mt-16 lg:py-0">
        <Reveal><EntryCard to="/ong" color="#1E7B34" logo={IMG.logoOng} icon={HeartPulse} name="ONG MA LEBA" title="Santé & premiers soins d'urgence"
          text="Premiers soins, consultations, vaccination et sensibilisation." testid="entry-card-ong" /></Reveal>
        <Reveal delay={0.1}><EntryCard to="/association" color="#C8161D" logo={IMG.logoAsso} icon={Sprout} name="Association MA LEBA" title="Agriculture & formation aux métiers"
          text="Groupement agricole et centre de formation professionnelle." testid="entry-card-association" /></Reveal>
      </section>

      <section className="pagne py-20 sm:py-28" data-testid="home-who-section">
        <div className="container-x grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="flex justify-center">
              <img src={IMG.maleba} alt="Ma Leba" loading="lazy" className="w-full max-w-md object-contain" data-testid="home-who-photo" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <SectionTitle eyebrow="Hommage" title="Qui était Ma Leba ?" />
            <Placeholder text={m.who_text} className="mt-6 text-lg text-neutral-700" />
            <Link to="/ma-leba" className="btn-u mt-8" data-testid="home-who-more">Découvrir son histoire <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="home-missions-section">
        <div className="container-x">
          <SectionTitle eyebrow="Nos missions communes" title="Une même inspiration, cinq engagements" />
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-black/5 bg-black/5 sm:grid-cols-2 lg:grid-cols-5">
            {MISSIONS.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.06} className="bg-white p-7">
                <Icon className={`h-10 w-10 ${["text-ong", "text-gold", "text-asso", "text-gold", "text-ong"][i]}`} aria-hidden="true" />
                <h3 className="mt-5 font-display text-xl font-bold uppercase leading-tight text-ink">{title}</h3>
                <p className="mt-2 text-neutral-600">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="home-news-section">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionTitle eyebrow="Actualités" title="Dernières nouvelles" />
            <Link to="/actualites" className="font-display font-semibold uppercase tracking-wide text-u hover:underline" data-testid="home-news-all">Toutes les actualités →</Link>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {(news || []).map((a, i) => <ArticleCard key={a.id} a={a} i={i} />)}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink py-20 text-white sm:py-24" data-testid="home-cta-section">
        <div className="absolute inset-0 pagne-light" />
        <div className="container-x relative grid gap-10 lg:grid-cols-2 lg:items-center">
          <SectionTitle light eyebrow="Agir avec nous" title="Ensemble, chaque geste compte" intro="Organisation, entreprise, bénévole ou donateur : rejoignez les communautés de Makana." />
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            {[["/devenir-partenaire", "Devenir partenaire", Handshake, "cta-partner"], ["/communication#benevole", "Devenir bénévole", HandHeart, "cta-volunteer"], ["/contact", "Nous contacter", Mail, "cta-contact"]].map(([to, l, Icon, t]) => (
              <Link key={to} to={to} data-testid={t} className="group flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 p-5 font-display text-lg font-semibold uppercase transition-colors hover:border-gold hover:bg-gold hover:text-ink">
                <Icon className="h-6 w-6 text-gold group-hover:text-ink" aria-hidden="true" /> {l}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
